/**
 * Format helpers for user preferences (currency, area unit).
 * preferredAreaUnit: 'sqm' | 'sqft'
 * preferredCurrency: 'USD' | 'EUR'
 */

const SQM_TO_SQFT = 10.7639;
const SQFT_TO_SQM = 1 / SQM_TO_SQFT;

/**
 * Get budget label suffix for min/max inputs (e.g. "Min ($)" or "Max (€)").
 * @param {string} currencyPreference - 'USD' | 'EUR'
 * @param {'min'|'max'} minOrMax
 */
export function getBudgetLabel(currencyPreference, minOrMax = 'min') {
  const cap = minOrMax === 'min' ? 'Min' : 'Max';
  const sym = (currencyPreference || 'USD') === 'EUR' ? '€' : '$';
  return `${cap} (${sym})`;
}

/**
 * Format area for display. Infers source unit from property (sqft vs sqm); converts if needed to preferred unit.
 * @param {{ sqft?: number, squareFeet?: string|number, sqm?: number }} property
 * @param {'sqm'|'sqft'} preferredUnit - user's area_unit_preference
 * @returns {string|null} e.g. "1,200 sqft" or "110 m²" or null
 */
export function formatArea(property, preferredUnit = 'sqm') {
  if (!property) return null;
  const preferSqft = preferredUnit === 'sqft';

  // Source value in sqft
  let valueSqft = null;
  if (property.sqft != null) {
    const n = Number(property.sqft);
    if (Number.isFinite(n) && n > 0) valueSqft = n;
  }
  if (valueSqft == null && property.squareFeet != null) {
    if (typeof property.squareFeet === 'string') {
      const m = property.squareFeet.replace(/\s/g, '').match(/([\d,]+)/);
      if (m) {
        const n = parseInt(m[1].replace(/,/g, ''), 10);
        if (Number.isFinite(n) && n > 0) valueSqft = n;
      }
    } else {
      const n = Number(property.squareFeet);
      if (Number.isFinite(n) && n > 0) valueSqft = n;
    }
  }

  // Source value in sqm
  let valueSqm = null;
  if (property.sqm != null) {
    const n = Number(property.sqm);
    if (Number.isFinite(n) && n > 0) valueSqm = n;
  }

  if (preferSqft) {
    const display = valueSqft != null ? valueSqft : (valueSqm != null ? Math.round(valueSqm * SQM_TO_SQFT) : null);
    if (display != null) return `${display.toLocaleString('en-US')} sqft`;
  } else {
    const display = valueSqm != null ? valueSqm : (valueSqft != null ? valueSqft * SQFT_TO_SQM : null);
    if (display != null) return `${Math.round(display)} m²`;
  }
  return null;
}

/**
 * Convert amount between EUR and USD using EUR_to_USD rate (1 EUR = rate USD).
 */
export function convertPrice(amount, fromCurrency, toCurrency, eurToUsd) {
  if (amount == null || !Number.isFinite(Number(amount)) || !eurToUsd || eurToUsd <= 0) return null;
  const n = Number(amount);
  const from = (fromCurrency || 'EUR').toUpperCase();
  const to = (toCurrency || 'EUR').toUpperCase();
  if (from === to) return n;
  if (from === 'EUR' && to === 'USD') return n * eurToUsd;
  if (from === 'USD' && to === 'EUR') return n / eurToUsd;
  return null;
}

/**
 * Round amount for display. roundMode: 'round' (nearest), 'floor' (down), 'ceil' (up).
 */
function roundAmount(amount, roundMode = 'round') {
  if (amount == null || !Number.isFinite(amount)) return null;
  const n = Number(amount);
  if (roundMode === 'floor') return Math.floor(n);
  if (roundMode === 'ceil') return Math.ceil(n);
  return Math.round(n);
}

/**
 * Parse a price string (e.g. "3.302,53 €" or "$1,234.56") to a number.
 */
function parsePriceString(str) {
  if (typeof str !== 'string' || !str.trim()) return null;
  const s = str.replace(/\s/g, '').replace(/[€$]/g, '');
  const match = s.match(/(\d[\d.,]*)/);
  if (!match) return null;
  let numStr = match[1];
  if (/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(numStr)) {
    numStr = numStr.replace(/,/g, '');
  } else if (/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(numStr)) {
    numStr = numStr.replace(/\./g, '').replace(',', '.');
  } else {
    numStr = numStr.replace(/,/g, '');
  }
  const n = parseFloat(numStr, 10);
  return Number.isFinite(n) ? n : null;
}

function formatRoundedCurrency(amount, prefCurrency, roundMode) {
  const rounded = roundAmount(amount, roundMode);
  if (rounded == null) return null;
  return new Intl.NumberFormat(prefCurrency === 'EUR' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency: prefCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(rounded);
}

/**
 * Format price for display using user's currency preference. Amounts are rounded to whole numbers.
 * If fxRate is provided and property.currency differs from preference, converts amount before formatting.
 * @param {{ price?: number, priceRaw?: string, currency?: string }} property
 * @param {'USD'|'EUR'} currencyPreference
 * @param {{ EUR_to_USD: number }|number|null} fxRate - optional; number or { EUR_to_USD }
 * @param {'round'|'floor'|'ceil'} roundMode - round to nearest, down, or up (default 'round')
 */
export function formatPrice(property, currencyPreference = 'USD', fxRate = null, roundMode = 'round') {
  const prefCurrency = (currencyPreference || 'USD') === 'EUR' ? 'EUR' : 'USD';
  const rate = typeof fxRate === 'number' ? fxRate : (fxRate?.EUR_to_USD ?? null);
  const propCurrency = (property?.currency || 'EUR').toUpperCase();

  if (typeof property?.price === 'number' && Number.isFinite(property.price)) {
    let amount = property.price;
    if (rate != null && propCurrency !== prefCurrency) {
      const converted = convertPrice(property.price, propCurrency, prefCurrency, rate);
      if (converted != null) amount = converted;
    }
    const formatted = formatRoundedCurrency(amount, prefCurrency, roundMode);
    if (formatted != null) return formatted;
  }

  const raw = (typeof property?.priceRaw === 'string' && property?.priceRaw?.trim()) ? property.priceRaw : null;
  if (raw) {
    const parsed = parsePriceString(raw);
    if (parsed != null) {
      const formatted = formatRoundedCurrency(parsed, prefCurrency, roundMode);
      if (formatted != null) return formatted;
    }
    if (prefCurrency === 'EUR') return raw.replace(/\$/g, '€').replace(/^US\$/g, '€');
    return raw.replace(/€/g, '$').replace(/^US\$/g, '$');
  }
  const price = property?.price;
  if (typeof price === 'string' && price.trim()) {
    const parsed = parsePriceString(price);
    if (parsed != null) {
      const formatted = formatRoundedCurrency(parsed, prefCurrency, roundMode);
      if (formatted != null) return formatted;
    }
    if (prefCurrency === 'EUR') return price.trim().replace(/\$/g, '€').replace(/^US\$/g, '€');
    return price.trim().replace(/€/g, '$').replace(/^US\$/g, '$');
  }
  return 'Price N/A';
}
